import type { RouteRecordRaw } from 'vue-router'

/**
 * Auth routes. Paths come from `docs/v2/route-map.md` — a route exists there before it is
 * built, so two phases cannot invent different paths for the same screen.
 *
 * `meta.public` marks a route the auth guard lets through unauthenticated. `/auth/password/set`
 * and `/auth/no-workspace` are **not** public: reaching either means you already signed in.
 */
export const authRoutes: RouteRecordRaw[] = [
  {
    path: '/auth/login',
    name: 'auth-login',
    component: () => import('./pages/LoginPage.vue'),
    meta: { layout: 'auth', public: true, title: 'auth.login.title' },
  },
  {
    // Phase 06. `?ref=APO-XXXXX` prefills the affiliate code (`modules/affiliate/constants`).
    path: '/auth/register',
    name: 'auth-register',
    component: () => import('./pages/RegisterPage.vue'),
    meta: { layout: 'auth', public: true, title: 'auth.register.title' },
  },
  {
    path: '/auth/otp',
    name: 'auth-otp',
    component: () => import('./pages/OtpPage.vue'),
    meta: { layout: 'auth', public: true, title: 'auth.otp.title' },
  },
  {
    path: '/auth/forgot-password',
    name: 'auth-forgot-password',
    component: () => import('./pages/ForgotPasswordPage.vue'),
    meta: { layout: 'auth', public: true, title: 'auth.reset.title' },
  },
  {
    // The welcome email's "Set your password" button, for someone an administrator added
    // (2026-10-12). Public: they have no password yet, and the link is how they get one.
    path: '/auth/welcome',
    name: 'auth-welcome',
    component: () => import('./pages/WelcomePage.vue'),
    meta: { layout: 'auth', public: true, title: 'auth.welcome.title' },
  },
  {
    // The WhatsApp invitation's "Set up my account" (2026-10-27, D27): the reference names the
    // invitation; the code the page sends is what proves the number.
    path: '/auth/activate/:ref',
    name: 'auth-activate',
    component: () => import('./pages/contacts').then((m) => m.ActivatePage),
    meta: { layout: 'auth', public: true },
  },
  {
    // The confirmation email's "Confirm my email" button (2026-10-27, D26). Signs nobody in.
    path: '/auth/confirm-email',
    name: 'auth-confirm-email',
    component: () => import('./pages/contacts').then((m) => m.ConfirmEmailPage),
    meta: { layout: 'auth', public: true },
  },
  {
    // A landlord confirms a WhatsApp number or email before the dashboard (2026-10-27, D25).
    path: '/auth/confirm',
    name: 'auth-confirm',
    component: () => import('./pages/contacts').then((m) => m.ConfirmContactPage),
    meta: { layout: 'auth', requiresAuth: true },
  },
  {
    path: '/auth/password/set',
    name: 'auth-password-set',
    component: () => import('./pages/PasswordSetPage.vue'),
    meta: { layout: 'auth', requiresAuth: true, title: 'auth.password.setTitle' },
  },
  {
    path: '/auth/no-workspace',
    name: 'auth-no-workspace',
    component: () => import('./pages/NoWorkspacePage.vue'),
    meta: { layout: 'auth', requiresAuth: true, title: 'auth.noWorkspace.title' },
  },
]

/**
 * `/app/settings/password` is kept as an address — the legacy bridge maps V1's password links to
 * it, and it may be bookmarked — but it is no longer a page: the guards send it to the active
 * workspace's profile, Security tab (2026-09-27), where every workspace changes its password.
 */
export const authSettingsRoutes: RouteRecordRaw[] = [
  {
    path: '/app/settings/password',
    name: 'settings-password',
    component: () => import('~/modules/platform/pages/RedirectingPage.vue'),
    meta: { layout: 'blank', requiresAuth: true },
  },
]
